import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { OrganizationLinkInvitationFragment } from "@ankaadia/graphql";
import { catchError, of } from "rxjs";
import { DeepLinkAccessService } from "../../features/deep-link-access/deep-link-access.service";
import { OrganizationLinkInvitationService } from "../../features/organizations/organization-link-invitation.service";
import { SettingsService } from "../../shared/services/settings.service";

/**
 * The email invitation link (`organization-link-invitation/:organizationId/:id/:token`) has to be a
 * route of its own, guarded by `PermissionGuard` only (any authenticated user, no role requirement),
 * because the backend has no idea which organization the recipient will actually be logged into when
 * they click it — that only becomes known once `PermissionGuard` has forced a login and this component
 * runs.
 *
 * Before handing off, this checks whether the current org is (or can become) the invitation's resolved
 * `targetOrganizationId` (see `OrganizationLinkInvitationService.resolveTargetOrganization` on the
 * backend) — switching into it via `DeepLinkAccessService.canAccessOrganizationLinkInvitation` if the
 * user has an account there but is logged into a different org, or sending them to the generic
 * access-denied page if they have no account in that org at all. Invitations predating
 * `targetOrganizationId`, and any failure to even load the invitation, fall back to the original
 * behavior below unchanged, letting the accept dialog's own `getByToken` call surface the real problem
 * (invalid/expired/withdrawn token).
 *
 * Once cleared, this redirects to the organizations settings page for the user's OWN organization,
 * carrying the invitation reference (`organizationId` of the SENDING org, `id`, `token`) as query params
 * — `NewUiOrganizationsComponent` picks those up and opens
 * `NewUiOrganizationLinkInvitationAcceptDialogComponent` for it. Same "resolve, then navigate, nothing to
 * render here" pattern as `NewUiOrgSwitchComponent`.
 */
@Component({
  selector: "new-ui-organization-link-invitation-redirect",
  template: "",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewUiOrganizationLinkInvitationRedirectComponent implements OnInit {
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly settings: SettingsService,
    private readonly invitationService: OrganizationLinkInvitationService,
    private readonly deepLinkAccessService: DeepLinkAccessService
  ) {}

  ngOnInit(): void {
    const { organizationId, id, token } = this.route.snapshot.params;

    this.invitationService
      .getByToken({ organizationId, id, token })
      .pipe(catchError(() => of(null)))
      .subscribe((invitation) => this.proceed(organizationId, id, token, invitation));
  }

  private proceed(
    organizationId: string,
    id: string,
    token: string,
    invitation: OrganizationLinkInvitationFragment | null
  ): void {
    const targetOrganizationId = invitation?.targetOrganizationId;

    if (!targetOrganizationId || targetOrganizationId === this.settings.organizationId) {
      this.goToSettings(organizationId, id, token);
      return;
    }

    this.deepLinkAccessService.canAccessOrganizationLinkInvitation(targetOrganizationId).subscribe((access) => {
      if (access.canAccess) {
        this.goToSettings(organizationId, id, token);
      } else if (access.redirectToOrganization) {
        this.settings.switchOrganizationKeepingCurrentUrl(access.redirectToOrganization);
      } else {
        this.settings.navigateToAccessDenied();
      }
    });
  }

  private goToSettings(organizationId: string, id: string, token: string): void {
    void this.router.navigate(["/app/settings/organizations", this.settings.organizationId], {
      queryParams: { invitationOrganizationId: organizationId, invitationId: id, invitationToken: token },
      replaceUrl: true,
    });
  }
}
